import { IssueSourceType } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { FC, useCallback, useMemo, useState } from 'react';

import {
  createCategoryWithTaskCountRenderer,
  createCreatorCellRenderer,
  createImagesCellRenderer,
  createProductsCellRenderer,
  createStatusCellRenderer,
  createStatusDateSummaryCellRenderer,
} from 'src/utils/issue/issue-ag-grid-renderers';
import {
  categoryCellStyle,
  categoryFilterValueFormatter,
  createAmountColumnGroup,
  handleIssuedByCellValueChanged,
  issueCaseStatusValueFormatter,
  issuedByCellEditorParams,
  issuedByValueFormatter,
  productsFilterParams,
  registeredAtValueFormatter,
  subcategoryValueGetter,
} from 'src/utils/issue/issue-ag-grid-util';
import { buildIssuePanelModalData, IssuePanelModalData, issueCaseStatusBadgeMap } from 'src/utils/issue/issue-util';

import Loading from 'src/components/Loading/Loading';

import { IssueManageMasterRowData } from '../IssueManage/IssueManage.types';
import { detailCellRendererParams } from '../IssueManage/IssueManageDetailGridConfig';
import IssuePanelModal from '../IssuePanelModal';

interface IssueStoreManagementTableProps {
  rowData: IssueManageMasterRowData[];
  isLoading: boolean;
}

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const IssueStoreManagementTable: FC<IssueStoreManagementTableProps> = ({ rowData, isLoading }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<IssuePanelModalData>(null);

  /**
   * 상세 버튼 클릭 핸들러
   * sourceType/sourceId를 기반으로 모달 props 결정
   */
  const handleOpenDetail = useCallback(
    (issueId: string, sourceType: IssueSourceType | null, sourceId: string | null) => {
      const data = buildIssuePanelModalData(issueId, sourceType, sourceId);
      setModalData(data);
      setModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalData(null);
  }, []);

  /**
   * 카테고리 요약 셀에 클릭 시 모달 열기 기능 추가
   */
  const CategorySummaryWithModalRenderer = useCallback(
    (params: ICellRendererParams<IssueManageMasterRowData>) => {
      const { _id, sourceType, sourceId, displayCategory } = params.data || {};

      if (!_id) return displayCategory || '-';

      return (
        <span
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => handleOpenDetail(_id, sourceType ?? null, sourceId ?? null)}>
          {displayCategory}
        </span>
      );
    },
    [handleOpenDetail]
  );

  /**
   * 카테고리 + 태스크 개수 렌더러 (클릭 시 모달 열기)
   */
  const CategoryWithTaskCountAndModalRenderer = useMemo(
    () =>
      createCategoryWithTaskCountRenderer<IssueManageMasterRowData>({
        onRowClick: (data) => handleOpenDetail(data._id, data.sourceType ?? null, data.sourceId ?? null),
      }),
    [handleOpenDetail]
  );

  const columnDefs = useMemo<(ColDef<IssueManageMasterRowData> | ColGroupDef<IssueManageMasterRowData>)[]>(() => {
    return [
      {
        headerName: '카테고리',
        children: [
          {
            field: 'displayCategory',
            headerName: '요약',
            width: 180,
            pinned: 'left',
            columnGroupShow: 'closed',
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
              innerRenderer: CategorySummaryWithModalRenderer,
            },
            cellStyle: categoryCellStyle,
          },
          {
            field: 'category',
            headerName: '분류',
            width: 160,
            pinned: 'left',
            columnGroupShow: 'open',
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
              innerRenderer: CategoryWithTaskCountAndModalRenderer,
            },
            cellStyle: categoryCellStyle,
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: categoryFilterValueFormatter,
              convertValuesToStrings: true,
            },
          },
          {
            field: 'subcategory',
            headerName: '세부분류',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            valueGetter: subcategoryValueGetter,
          },
        ],
      },
      {
        headerName: '상태/일시',
        children: [
          {
            headerName: '요약',
            width: 160,
            columnGroupShow: 'closed',
            cellRenderer: createStatusDateSummaryCellRenderer<IssueManageMasterRowData>(issueCaseStatusBadgeMap),
            filter: false,
            sortable: false,
          },
          {
            field: 'status',
            headerName: '상태',
            width: 120,
            columnGroupShow: 'open',
            cellRenderer: createStatusCellRenderer<IssueManageMasterRowData>(issueCaseStatusBadgeMap),
            filterParams: {
              valueFormatter: issueCaseStatusValueFormatter,
            },
          },
          {
            field: 'registeredAt',
            headerName: '등록일시',
            width: 140,
            columnGroupShow: 'open',
            valueFormatter: registeredAtValueFormatter,
          },
        ],
      },
      {
        headerName: '이슈 상품',
        width: 200,
        cellRenderer: createProductsCellRenderer<IssueManageMasterRowData>(),
        filter: 'agSetColumnFilter',
        filterParams: productsFilterParams,
      },
      {
        headerName: '이미지',
        width: 100,
        cellRenderer: createImagesCellRenderer<IssueManageMasterRowData>(),
        filter: false,
        sortable: false,
      },
      createAmountColumnGroup(),
      {
        field: 'publicNote',
        headerName: '메모(공개)',
        width: 120,
      },
      {
        field: 'privateNote',
        headerName: '메모(비공개)',
        width: 120,
      },
      {
        headerName: '작성자',
        field: 'creatorName',
        width: 100,
        cellRenderer: createCreatorCellRenderer<IssueManageMasterRowData>(),
      },
      {
        field: 'issuedBy',
        headerName: '출처',
        width: 100,
        editable: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        cellEditorParams: issuedByCellEditorParams,
        valueFormatter: issuedByValueFormatter,
        onCellValueChanged: handleIssuedByCellValueChanged as ColDef['onCellValueChanged'],
      },
    ];
  }, [CategorySummaryWithModalRenderer, CategoryWithTaskCountAndModalRenderer]);

  return (
    <>
      <div className='ag-theme-alpine height100'>
        {isLoading && <Loading title='데이터를 불러오고 있습니다.' />}
        <AgGridReact<IssueManageMasterRowData>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data._id}
          isRowMaster={(dataItem) => (dataItem ? dataItem.issueTasks.length > 0 : false)}
          enableCellTextSelection={true}
          tooltipShowDelay={0}
          animateRows={true}
          // DetailRow
          keepDetailRows={true}
          masterDetail={true}
          detailRowAutoHeight={true}
          detailCellRendererParams={detailCellRendererParams}
        />
      </div>
      {modalData && <IssuePanelModal open={modalOpen} onClose={handleCloseModal} {...modalData} />}
    </>
  );
};

export default IssueStoreManagementTable;
